import * as Sentry from '@sentry/react';

import { fnv1a } from '@vestigas/utils';

const STORAGE_PREFIX = 'sentry-rv:';
const TTL_MS = 24 * 60 * 60 * 1000;
// Both daily caps are counted per endpoint, never per (endpoint, digest) —
// keying them by digest would multiply the ceiling by the number of distinct
// failure shapes, which is exactly the storm the caps exist to prevent.
const NEW_DIGEST_DAILY_CAP = 3;
// PER BROWSER PROFILE, not global — dedup state lives in localStorage and
// cannot coordinate across the fleet. Total daily volume for one drifted
// endpoint is therefore ~20 x affected browsers, so sizing the Sentry quota
// impact needs that multiplication, not this number on its own.
const ENTITY_DAILY_CAP = 20;

type ResponseValidationProps = {
  /**
   * Opaque failure-shape id computed by the CALLER (fnv1a of the sorted
   * unique normalized path:code pairs). A dedup key and a `shape` tag — it
   * does NOT drive Sentry grouping.
   */
  digest: string;
  /** Optional subject of the failing response; omit for lists. */
  entityId?: string;
  /**
   * Already-sanitized zod issues, produced by the caller. Forwarded into
   * `extra` without inspection — the logger performs no redaction.
   *
   * Required: a safeParse failure always has issues, and an event without them
   * carries no debugging payload at all. A caller with nothing to say passes
   * `[]` deliberately rather than forgetting the field silently.
   *
   * NOTE: `unknown[]` does NOT enforce that these were sanitized — a raw
   * `error.issues` satisfies it. The contract is the caller's to keep; only a
   * branded type produced by the sanitizer itself would make it a type error.
   */
  issues: unknown[];
  /** Stable per-endpoint key, kebab-case. Drives grouping and dedup. */
  key: string;
  message: string;
  /**
   * Issue count BEFORE truncation, so a reader can tell "500 rows failed, you
   * are seeing 10" from "10 rows failed". Flat rather than nested inside
   * `issues` so Sentry renders it at the top level of `extra`.
   */
  totalIssues: number;
};

type DedupMap = Record<string, number>;

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const localDateStamp = (now: number): string => {
  const date = new Date(now);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${date.getFullYear()}-${month}-${day}`;
};

const sanitizeMap = (raw: unknown, now: number, today: string): DedupMap => {
  if (!isPlainRecord(raw)) {
    return {};
  }

  const map: DedupMap = {};

  for (const [entryKey, value] of Object.entries(raw)) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      // Corrupt entry — treat as absent so it can never suppress reporting
      // forever.
      continue;
    }

    if (entryKey.startsWith('#')) {
      // Reserved cap-counter keys hold COUNTS, not timestamps — never prune
      // them by comparing against `now`; stale-dated ones are dropped here.
      if (entryKey === `#new:${today}` || entryKey === `#ent:${today}`) {
        map[entryKey] = value;
      }
      continue;
    }

    if (value <= now) {
      // Expired (digest, entityHash) pair — pruned on read and write.
      continue;
    }

    // Clamp so a past clock jump can never disable reporting indefinitely.
    map[entryKey] = Math.min(value, now + TTL_MS);
  }

  return map;
};

const createDedupStore = () => {
  // In-memory fallback for storage-blocked browsers. Closure-held per logger
  // instance, deliberately not module scope.
  const fallback = new Map<string, DedupMap>();
  // A throwing setItem with a WORKING getItem (QuotaExceededError — the quota
  // is per-origin, so an unrelated tab can exhaust it) would otherwise leave
  // reads resurrecting the last persisted map forever: pairs never get marked,
  // the counters never advance, and every later call captures. Latch to memory
  // for the rest of the session on the first failed write.
  let isStorageWritable = true;

  const read = (storageKey: string): unknown => {
    if (!isStorageWritable) {
      return fallback.get(storageKey) ?? {};
    }

    try {
      const raw = globalThis.localStorage.getItem(storageKey);

      return raw === null ? {} : (JSON.parse(raw) as unknown);
    } catch {
      return fallback.get(storageKey) ?? {};
    }
  };

  const write = (storageKey: string, map: DedupMap): void => {
    fallback.set(storageKey, map);
    if (!isStorageWritable) {
      return;
    }

    try {
      globalThis.localStorage.setItem(storageKey, JSON.stringify(map));
    } catch {
      // Storage unavailable — the in-memory fallback above keeps dedup
      // working for the rest of the session.
      isStorageWritable = false;
    }
  };

  return { read, write };
};

// Reserved cap counters are absent until the first capture of the day.
const counterValue = (map: DedupMap, counterKey: string): number =>
  map[counterKey] ?? 0;

// Has this endpoint already reported ANY entity under this failure shape?
// Reserved `#`-prefixed cap counters are not pairs and never match.
const hasPairForDigest = (map: DedupMap, digest: string): boolean =>
  Object.keys(map).some(
    (entryKey) =>
      !entryKey.startsWith('#') && entryKey.startsWith(`${digest}:`),
  );

// Long-term dedup policy: decides whether this failure SHAPE (+ entity) was
// already reported recently (localStorage, 24h TTL, novelty-tiered capture).
// Persistent on purpose.
// Do NOT replace with a per-session budget and do NOT merge with the session
// flood fuse in packages/sentry guards (different job: that one is an
// incident circuit breaker, this one is a permanent reporting policy).
const decideAndMark = ({
  digest,
  map,
  now,
  pairKey,
  sampleRate,
  today,
}: {
  digest: string;
  map: DedupMap;
  now: number;
  pairKey: string;
  sampleRate: number;
  today: string;
}): boolean => {
  const entityCounterKey = `#ent:${today}`;
  const entityCount = counterValue(map, entityCounterKey);
  // ONE budget for every tier: new-digest captures, first-seen entity
  // captures and heartbeats all draw from it, so ~20 events per endpoint per
  // browser per day is a hard TOTAL bound with no heartbeat side channel.
  const hasEntityHeadroom = entityCount < ENTITY_DAILY_CAP;

  if (pairKey in map) {
    // Repeat within the 24h TTL: heartbeat only. Gated by the daily cap
    // (checked BEFORE the dice roll, so a lost roll never consumes budget)
    // and counting against it. Deliberately does NOT refresh the pair's
    // expiresAt — a sliding TTL would let a hot endpoint starve its own next
    // guaranteed per-asset capture.
    if (!hasEntityHeadroom || Math.random() >= sampleRate) {
      return false;
    }

    map[entityCounterKey] = entityCount + 1;

    return true;
  }

  const isKnownDigest = hasPairForDigest(map, digest);
  const newCounterKey = `#new:${today}`;
  const newCount = counterValue(map, newCounterKey);

  if (
    !hasEntityHeadroom ||
    (!isKnownDigest && newCount >= NEW_DIGEST_DAILY_CAP)
  ) {
    // Cap-dropped: the pair is NOT marked as reported, so a suppressed asset
    // stays eligible and lands when headroom returns. The cap defers
    // coverage during a storm; it never cancels it.
    return false;
  }

  // Pairs are marked only on successful capture. A new-digest capture also
  // counts against the daily entity cap (one budget, not two).
  map[pairKey] = now + TTL_MS;
  map[entityCounterKey] = entityCount + 1;
  if (!isKnownDigest) {
    map[newCounterKey] = newCount + 1;
  }

  return true;
};

export const createResponseValidationReporter = ({
  isProduction,
  sampleRate,
}: {
  isProduction: boolean;
  sampleRate: number;
}) => {
  const store = createDedupStore();

  const report = ({
    digest,
    entityId,
    issues,
    key,
    message,
    totalIssues,
  }: ResponseValidationProps): void => {
    if (!isProduction) {
      console.warn(message, { digest, entityId, issues, key, totalIssues });
    }

    if (!Sentry.isInitialized()) {
      return;
    }

    // Breadcrumb on EVERY call — key + digest ONLY, never the payload.
    // Breadcrumbs ride along on every subsequent event in the session, so
    // values here would leak into unrelated error reports and bypass the
    // capture policy.
    Sentry.addBreadcrumb({
      category: 'response-validation',
      data: { digest, key },
      level: 'warning',
      message,
      type: 'info',
    });

    const now = Date.now();
    const today = localDateStamp(now);
    const storageKey = `${STORAGE_PREFIX}${key}`;
    const map = sanitizeMap(store.read(storageKey), now, today);

    // Never store the raw entityId — localStorage survives logout and shared
    // workstations. The Sentry `entity_id` tag stays the plain id on purpose.
    const entityHash = entityId === undefined ? '' : fnv1a(entityId);
    const pairKey = `${digest}:${entityHash}`;

    const shouldCapture = decideAndMark({
      digest,
      map,
      now,
      pairKey,
      sampleRate,
      today,
    });

    store.write(storageKey, map);

    if (!shouldCapture) {
      return;
    }

    Sentry.captureMessage(message, {
      extra: { digest, issues, totalIssues },
      // Grouping is per endpoint, deliberately: you fix an endpoint's
      // schema; you enumerate affected assets and shapes via the tags.
      fingerprint: ['response-validation', key],
      level: 'warning',
      tags: {
        ...(entityId === undefined ? {} : { entity_id: entityId }),
        response_validation: key,
        shape: digest,
      },
    });
  };

  return (props: ResponseValidationProps): void => {
    try {
      report(props);
    } catch {
      // A failed report has nowhere left to go, so nothing thrown while
      // reporting may reach the caller.
    }
  };
};
